import { PlanFeatureCategory, PlanKey } from './index.types';

export const PLAN_COLUMNS: readonly PlanKey[] = [
  'Free',
  'Basic',
  'Pro',
  'Pro +',
];

const yes = { kind: 'included' } as const;
const no = { kind: 'excluded' } as const;
const partial = { kind: 'partial' } as const;
const na = { kind: 'notApplicable' } as const;
const val = (text: string) => ({ kind: 'value', text } as const);

const TOOLTIP = {
  fileUpdate: ['세일즈클루 링크는 그대로 두고,', '파일만 변경이 가능합니다.'],
  formEmailVerify: [
    '문의양식 제출 시 이메일 인증을 요구하여',
    '스팸 제출을 방지합니다.',
  ],
  customLink: [
    'sclu.io/s/ 뒤의 주소를 원하는 이름으로 바꿔',
    '기억하기 쉬운 링크로 공유할 수 있어요.',
  ],
  customDomain: [
    '자료를 보낼 때, sclu.io/ 대신',
    '커스텀 도메인으로 시작하는 링크를 만들 수 있어요.',
  ],
  campaignQuota: [
    '발송량은 구독 시작일 기준으로 매월 초기화되며,',
    '미사용량은 이월되지 않습니다.',
    '초과 발송은 애드온으로 추가할 수 있습니다.',
  ],
} as const;

export const PLAN_FEATURE_MATRIX: readonly PlanFeatureCategory[] = [
  {
    id: 'pdf',
    title: 'PDF 추적 및 관리',
    rows: [
      {
        id: 'pdf-upload-size',
        label: 'PDF 파일 업로드 최대 용량',
        values: {
          Free: val('100MB'),
          Basic: val('100MB'),
          Pro: val('500MB'),
          'Pro +': val('500MB'),
        },
      },
      {
        id: 'pdf-upload-count',
        label: 'PDF 파일 업로드 개수',
        values: {
          Free: val('최대 3개'),
          Basic: val('최대 10개'),
          Pro: val('무제한'),
          'Pro +': val('무제한'),
        },
      },
      {
        id: 'pdf-cta-button',
        label: 'PDF 내 CTA 버튼 추가',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-message-box',
        label: 'PDF 내 메시지 박스 추가',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-video',
        label: 'PDF 내 동영상 추가',
        values: {
          Free: val('계정당 1개'),
          Basic: val('PDF당 3개'),
          Pro: val('PDF당 10개'),
          'Pro +': val('무제한'),
        },
      },
      {
        id: 'pdf-form',
        label: 'PDF 내 문의양식 커스텀',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-form-cover',
        label: '문의양식에 가림막 기능 더하기',
        values: {
          Free: partial,
          Basic: partial,
          Pro: val('고급 설정'),
          'Pro +': val('고급 설정'),
        },
      },
      {
        id: 'pdf-form-email-verify',
        label: '문의양식 이메일 인증',
        tooltip: TOOLTIP.formEmailVerify,
        values: { Free: no, Basic: no, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-page-duration',
        label: '페이지별 열람 시간 분석',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-link-click',
        label: '링크 클릭 데이터 추적',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-share-count',
        label: '파일 공유 횟수 추적',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-download-track',
        label: 'PDF 다운로드 여부 추적',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-referrer',
        label: '유입 경로 분석 (검색·소셜·이메일 등)',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-geo',
        label: '열람자 국가·도시 정보',
        values: { Free: no, Basic: no, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-history-range',
        label: '열람 기록 조회 기간',
        values: {
          Free: val('7일'),
          Basic: val('전체 기간'),
          Pro: val('전체 기간'),
          'Pro +': val('전체 기간'),
        },
      },
      {
        id: 'pdf-email-alert',
        label: '실시간 이메일 알림',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-zapier',
        label: 'Zapier 연동',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-csv',
        label: 'CSV 다운로드',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-bot-filter',
        label: '봇 탐지 및 필터링',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-slack-alert',
        label: '실시간 슬랙 알림',
        values: { Free: no, Basic: no, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-file-update',
        label: 'PDF 파일 업데이트 (링크 유지)',
        tooltip: TOOLTIP.fileUpdate,
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-download-block',
        label: 'PDF 다운로드 비허용 설정',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-badge-remove',
        label: '뷰어 내 세일즈클루 배너 제거',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-utm',
        label: 'UTM 추적',
        values: { Free: no, Basic: no, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-ga-gtm',
        label: 'Google Analytics / GTM 연동',
        values: { Free: no, Basic: no, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-custom-link',
        label: '커스텀 링크',
        tooltip: TOOLTIP.customLink,
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'pdf-custom-domain',
        label: '커스텀 도메인',
        tooltip: TOOLTIP.customDomain,
        values: { Free: no, Basic: no, Pro: no, 'Pro +': yes },
      },
    ],
  },
  {
    id: 'link',
    title: '링크 추적 및 관리',
    rows: [
      {
        id: 'link-count',
        label: '생성 가능한 링크 수',
        values: {
          Free: val('3개'),
          Basic: val('10개'),
          Pro: val('100개'),
          'Pro +': val('1,000개'),
        },
      },
      {
        id: 'link-history-range',
        label: '열람 기록 조회 기간',
        values: {
          Free: val('7일'),
          Basic: val('전체 기간'),
          Pro: val('전체 기간'),
          'Pro +': val('전체 기간'),
        },
      },
      {
        id: 'link-referrer',
        label: '유입 경로 분석 (검색·소셜·이메일 등)',
        values: { Free: yes, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'link-url-update',
        label: '링크 URL 업데이트',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'link-og-custom',
        label: 'Open Graph 커스텀',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'link-bot-filter',
        label: '봇 탐지 및 필터링',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'link-geo',
        label: '열람자 국가·도시 정보',
        values: { Free: no, Basic: no, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'link-utm',
        label: 'UTM 추적',
        values: { Free: no, Basic: no, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'link-custom-link',
        label: '커스텀 링크',
        tooltip: TOOLTIP.customLink,
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'link-custom-domain',
        label: '커스텀 도메인',
        tooltip: TOOLTIP.customDomain,
        values: { Free: no, Basic: no, Pro: no, 'Pro +': yes },
      },
    ],
  },
  {
    id: 'client',
    title: '고객 관리',
    rows: [
      {
        id: 'client-count',
        label: '등록 가능한 고객 수',
        values: {
          Free: val('10명'),
          Basic: val('100명'),
          Pro: val('1,000명'),
          'Pro +': val('10,000명'),
        },
      },
    ],
  },
  {
    id: 'campaign',
    title: '이메일 캠페인',
    badge: 'Beta',
    rows: [
      {
        id: 'campaign-quota',
        label: '기본 발송량',
        tooltip: TOOLTIP.campaignQuota,
        values: {
          Free: na,
          Basic: val('20'),
          Pro: val('50'),
          'Pro +': val('100'),
        },
      },
      {
        id: 'campaign-sender-verify',
        label: '발신 이메일 인증',
        values: {
          Free: na,
          Basic: val('1개'),
          Pro: val('1개'),
          'Pro +': val('1개'),
        },
      },
      {
        id: 'campaign-schedule',
        label: '예약 발송',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
      {
        id: 'campaign-tracking',
        label: '전달·오픈·링크 클릭·열람 확인',
        values: { Free: no, Basic: yes, Pro: yes, 'Pro +': yes },
      },
    ],
  },
];

/**
 * 플랜명을 비교표 열 키로 정규화한다.
 *
 * API는 'Pro Plus'를 내려주고 PricePlanList가 이를 'Pro +'로 바꿔 atom에 넣는다.
 * 비교표가 그 변환에 의존하지 않도록 두 표기를 모두 받는다.
 */
export const normalizePlanName = (name?: string): PlanKey | null => {
  if (!name) {
    return null;
  }

  const normalized = name.trim();

  if (normalized === 'Pro Plus' || normalized === 'Pro+') {
    return 'Pro +';
  }

  return PLAN_COLUMNS.find((plan) => plan === normalized) ?? null;
};
